import React, { memo, useEffect, useMemo } from 'react'

import {
  Notice,
  Icon,
  RotatingSpinner,
  CircleCheck,
  CircleX,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { dismissDccNotification } from 'Pods/ThreeDStudio/Store/actions/dccBridge'
import { DCC_SEND_STATUS } from 'Pods/ThreeDStudio/Store/slices/dccBridge'

const AUTO_HIDE_MS = 15000

const buildIcon = (svg, color) => (
  <Icon
    svgComponent={svg}
    color={color}
    notSpaced
    size={iconSizeMedium}
  />
)

// The plugin-not-running description reads differently depending on whether the
// target DCC name is known, so it is resolved from the status in render.
const VARIANT_BY_STATUS = {
  [DCC_SEND_STATUS.CONNECTING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    titleKey: 'shared.dccNotification.connecting',
    descriptionKey: null,
    dismissible: false,
  },
  [DCC_SEND_STATUS.SENDING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    titleKey: 'shared.dccNotification.sending',
    descriptionKey: null,
    dismissible: false,
  },
  [DCC_SEND_STATUS.SUCCESS]: {
    type: 'positive',
    icon: buildIcon(CircleCheck, 'positive'),
    titleKey: 'shared.dccNotification.success',
    descriptionKey: null,
    dismissible: true,
  },
  [DCC_SEND_STATUS.PLUGIN_NOT_RUNNING]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    titleKey: 'shared.dccNotification.pluginNotRunning.title',
    descriptionKey: 'pluginNotRunning',
    dismissible: true,
  },
  [DCC_SEND_STATUS.DCC_BUSY]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    titleKey: 'shared.dccNotification.dccBusy.title',
    descriptionKey: 'shared.dccNotification.dccBusy.description',
    dismissible: true,
  },
  [DCC_SEND_STATUS.ERROR]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    titleKey: 'shared.dccNotification.error.title',
    descriptionKey: 'shared.dccNotification.error.description',
    dismissible: true,
  },
}

function DccNotification() {
  const t = useTranslation()
  const sendStatus = useThreeDStudioStore(state => state.dccBridge.sendStatus)
  const sendingTo = useThreeDStudioStore(state => state.dccBridge.sendingTo)

  const variant = useMemo(() => VARIANT_BY_STATUS[sendStatus], [sendStatus])

  useEffect(() => {
    if (!variant) return undefined
    const timer = setTimeout(dismissDccNotification, AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [variant, sendingTo])

  if (!variant) return null

  const title = t(variant.titleKey, { name: sendingTo })
  const resolveDescription = () => {
    if (variant.descriptionKey === 'pluginNotRunning') {
      return sendingTo
        ? t('shared.dccNotification.pluginNotRunning.descriptionWithName', { name: sendingTo })
        : t('shared.dccNotification.pluginNotRunning.description')
    }
    return variant.descriptionKey ? t(variant.descriptionKey) : null
  }
  const description = resolveDescription()
  return (
    <div
      className={classnames('dcc-notification', description && 'dcc-notification--with-description' )}
      role="status"
      aria-live="polite"
    >
      <Notice
        type={variant.type}
        useSSRIcon
        ssrIcon={variant.icon}
        onClose={variant.dismissible ? dismissDccNotification : null}
      >
        <div className="dcc-notification__body">
          <span className="cgt-heading-50">{title}</span>
          {description && <span className="cgt-body-base">{description}</span>}
        </div>
      </Notice>
    </div>
  )
}

export default memo(DccNotification)
